import React, { useEffect, useState ,useMemo} from "react";
import {
  Box,
  Button,
  MenuItem,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ColDef } from "ag-grid-community";
import { AgGridTable } from "../../Component/AgGridTable";
import axiosInstance from "../../services/axiosInstance";
import {
  fetchCustomers,
  fetchGetMonths,
  fetchGetYears,
  GetProjectByCustomerId,
  CreateInvoice,
} from "../../services/InvoiceServices";

interface AddInvoiceTableProps {
  drawerOpen: boolean;
}

type DropdownOption = {
  tableID: number;
  tableName: string;
};

const AddInvoice: React.FC<AddInvoiceTableProps> = ({ drawerOpen }) => {
  const [addTableData, setAddTableData] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [years, setYears] = useState<DropdownOption[]>([]);
  const [months, setMonths] = useState<DropdownOption[]>([]);
  const [customer, setCustomer] = useState<number | "">("");
  const [project, setProject] = useState<number | "">("");
  const [month, setMonth] = useState<number | "">("");
  const [year, setYear] = useState<number | "">("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning",
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
    const gridStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const invoiceColumns: ColDef[] = [
    { field: "employeeCode", headerName: "EMPCode", editable: false, width: 100, maxWidth: 100 },
    { field: "firstName", headerName: "First Name", editable: false,width: 200, suppressSizeToFit: true  },
    { field: "lastName", headerName: "Last Name", editable: false,width: 200, suppressSizeToFit: true  },
    {
      field: "bookedHours",
      headerName: "Hours",
      editable: true,
      cellClass: "right-align",
       width: 100, maxWidth: 100
    },
    {
      field: "rate",
      headerName: "Rate",
      editable: false,
      cellClass: "right-align",
       width: 100, maxWidth: 100
    },
    {
      field: "invoiceAmount",
      headerName: "Amount",
      editable: false,
      cellClass: "right-align",
      // valueGetter: (params) => {
      //   const rate = params.data.rate;
      //   const hours = params.data.bookedHours;
      //   return rate && hours ? rate * hours : "";
      // },
       width: 100, maxWidth: 100
    },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        const customerData = await fetchCustomers();
        setCustomers(Array.isArray(customerData.data) ? customerData.data : []);

        const yearData = await fetchGetYears();
        setYears(Array.isArray(yearData.data) ? yearData.data : []);

        const monthData = await fetchGetMonths();
        setMonths(Array.isArray(monthData.data) ? monthData.data : []);

        const currentDate = new Date();
        const currentMonthName = currentDate.toLocaleString("default", {
          month: "short",
        });
        const currentYear = currentDate.getFullYear();

        const matchedMonth = monthData.data.find(
          (m: DropdownOption) =>
            m.tableName.toLowerCase() === currentMonthName.toLowerCase()
        );
        const matchedYear = yearData.data.find(
          (y: DropdownOption) => y.tableName === currentYear.toString()
        );

        if (!month && matchedMonth) setMonth(matchedMonth.tableID);
        if (!year && matchedYear) setYear(matchedYear.tableID);
      } catch (error) {
        console.error("Failed to load filter options:", error);
      }
    };
    loadData();
  }, []);

  const handleGo = async () => {
  if (!customer || !project || !month || !year) {
    setSnackbar({
      open: true,
      message: "Please select all filters.",
      severity: "warning",
    });
    return;
  }

  try {
    const response = await axiosInstance.get("/GetNewInvoiceDetails", {
      params: {
        projectId: project,
        customerId: customer,
        yearId: year,
        monthId: month,
      },
    });

    const detailArray: any = response?.data?.data;

    if (!Array.isArray(detailArray) || detailArray.length === 0) {
      setAddTableData([]);
      setSnackbar({
        open: true,
        message: "Timesheet doesn't exist for the selected filters!",
        severity: "warning",
      });
      return;
    }

    
    if (detailArray[0]?.message === "Invoice already exists.") {
      setAddTableData([]);  
      setSnackbar({
        open: true,
        message: "Invoice already exists for the selected filters.",
        severity: "warning",
      });
      return;
    }

    
    setAddTableData(
      detailArray.map((item: any) => ({
        employeeId: item.employeeId,
        invoiceId: item.invoiceId,
        employeeCode: item.employeeCode,
        firstName: item.firstName,
        lastName: item.lastName,
        rate: item.rate,
        bookedHours: item.bookedHours,
        timesheetId: item.timesheetId,
        invoiceAmount: item.invoiceAmount,
      }))
    );

  } catch (error) {
    console.error("Error fetching invoice data:", error);
    setSnackbar({
      open: true,
      message: "Failed to fetch invoice data.",
      severity: "error",
    });
  }
};

  const handleReset = () => {
    setCustomer("");
    setProject("");
    setMonth("");
    setYear("");
    setProjects([]);
    setAddTableData([]);
  };

  const handleSave = () => {
    if (!customer || !project || !month || !year) {
      setSnackbar({
        open: true,
        message: "Please select all filters before saving.",
        severity: "warning",
      });
      return;
    }
    setOpenConfirmDialog(true);
  };

  const confirmSave = async () => {
    setOpenConfirmDialog(false);

    const invoiceDetailRequestModel = addTableData.map((item) => ({
      employeeId: item.employeeId,
      employeeCode: item.employeeCode,
      firstName: item.firstName ,
      lastName: item.lastName,
      bookedHours: item.bookedHours,
      invoiceId: item.Id,
      rate: item.rate,
      invoiceAmount:
        item.bookedHours && item.rate ? item.bookedHours * item.rate : 0,

      // invoiceAmount: item.bookedHours && item.rate && item.bookedHours * item.rate

    }));

    const payload = {
      invoiceId: id,
      projectId: project,
      customerId: customer,
      monthId: month,
      yearId: year,
      timesheetID: addTableData[0]?.timesheetId ?? null,
      invoiceDetailRequestModel,
    };

    try {
      const response = await CreateInvoice(payload);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.data?.success
      ) {
        setSnackbar({
          open: true,
          message: "Invoice saved successfully!",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/InvoiceTable");
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: "Something went wrong while saving.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      setSnackbar({
        open: true,
        message: "Failed to save Invoice. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h5" fontWeight="bold">
          Add Invoice
        </Typography>
      </Box>

      <Box display="flex" gap={3}>
        <Box
          width="300px"
          minWidth={250}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            paddingRight: 2,
            backgroundColor: "#fff",
            padding: 3,
            borderRadius: 3,
            paddingTop: "18px",
            paddingBottom: "6px",
          }}
        >
          {[
            {
              label: "Customer",
              value: customer,
              onChange: async (e: any) => {
                const selectedCustomerId = Number(e.target.value);
                setCustomer(selectedCustomerId);
                setProject("");
                setProjects([]);
                try {
                  const response = await GetProjectByCustomerId(
                    selectedCustomerId
                  );
                  setProjects(
                    Array.isArray(response.data?.data)
                      ? response.data.data
                      : []
                  );
                } catch (error) {
                  console.error("Error fetching projects for customer:", error);
                  setProjects([]);
                }
              },
              options: customers,
              getValue: (c: any) => c.customerId,
              getName: (c: any) => c.customerName,
              disabled: !!id,
            },
            {
              label: "Project",
              value: project,
              onChange: (e: any) => setProject(Number(e.target.value)),
              options: projects,
              getValue: (p: any) => p.projectId,
              getName: (p: any) => p.projectName,
              disabled: !!id,
            },
            {
              label: "Month",
              value: month,
              onChange: (e: any) => setMonth(Number(e.target.value)),
              options: months,
              getValue: (m: any) => m.tableID,
              getName: (m: any) => m.tableName,
            },
            {
              label: "Year",
              value: year,
              onChange: (e: any) => setYear(Number(e.target.value)),
              options: years,
              getValue: (y: any) => y.tableID,
              getName: (y: any) => y.tableName,
            },
          ].map(
            ({
              label,
              value,
              onChange,
              options,
              getValue,
              getName,
              disabled,
            }) => (
              <FormControl fullWidth size="small" key={label}>
                <InputLabel>{label}</InputLabel>
                <Select
                  value={value}
                  label={label}
                  onChange={onChange}
                  disabled={disabled}
                >
                  {options.length === 0 ? (
                    <MenuItem disabled>No {label} available</MenuItem>
                  ) : (
                    options.map((opt: any) => (
                      <MenuItem key={getValue(opt)} value={getValue(opt)}>
                        {getName(opt)}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )
          )}

          <Button variant="contained" onClick={handleGo}>
            Go
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
        </Box>

        <Box flex={1}>
          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            columns={invoiceColumns}
            data={addTableData}
            pagination={false}
            rowSelection="single"
            paginationPerPage={10}
            noDataComponentText="No invoice data loaded"
            handleSelectedRows={() => {}}
            gridOptions={{
              defaultColDef: {
                editable: true,
                resizable: true,
                flex: 1,
              },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          zIndex: 1300,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to save the invoice?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmSave} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddInvoice;
